import { useEffect, useState } from 'react';
import { ReactComponent as LocationIcon } from '@assets/icons/location.svg';
import PropTypes from 'prop-types';
import { ReactComponent as ErrorIcon } from '@assets/icons/empty_error_icon.svg';
import { ReactComponent as ArrowLeftIcon } from '@assets/images/left_icon.svg';
import { useOrgStore } from '@stores';
import clsx from 'clsx';
import { Button } from '@material-tailwind/react';

// Interface for location data that will be passed to parent component
export interface LocationData {
  accuracy: number;
  latitude: number;
  longitude: number;
  timestamp: number;
}

// Props interface for the LocationPrompt component
interface LocationPromptProps {
  // Callback function when location access is granted
  onLocationGranted: (locationData: LocationData) => void;
  // Callback function to trigger photo upload
  onUpload: () => void;
  // Location sharing configuration mode
  locationSharing?: 'MANDATORY' | 'WARNING_PROMPT' | 'SILENT';
  // Callback function when user chooses to continue without location (for WARNING_PROMPT mode)
  onContinueWithoutLocation?: () => void;
  // Callback function to set that user has denied location in WARNING_PROMPT mode
  onLocationDenied?: () => void;
  // Callback function when back button is pressed
  onBack?: () => void;
}

// Enum for different states of location permission
enum LocationPermissionState {
  UNKNOWN = 'unknown',
  GRANTED = 'granted',
  DENIED = 'denied',
  PROMPT = 'prompt',
  BLOCKED = 'blocked'
}

export const LocationPrompt: React.FC<LocationPromptProps> = ({
  onLocationGranted,
  onUpload,
  locationSharing = 'MANDATORY',
  onContinueWithoutLocation,
  onLocationDenied,
  onBack
}) => {
  // State to control permission error visibility
  const [showPermissionError, setShowPermissionError] = useState(false);

  const [permissionState, setPermissionState] =
    useState<LocationPermissionState>(LocationPermissionState.UNKNOWN);
  const { organisation } = useOrgStore();
  const [primaryColor, setPrimaryColor] = useState(organisation.primaryColour);
  const isWarningPrompt = locationSharing === 'WARNING_PROMPT';

  useEffect(() => {
    setPrimaryColor('#' + organisation.primaryColour);
  }, [organisation.primaryColour]);
  // Check current permission status
  const checkPermission = async () => {
    try {
      if (!navigator.permissions || !navigator.permissions.query) {
        return;
      }

      const result = await navigator.permissions.query({ name: 'geolocation' });
      setPermissionState(result.state as LocationPermissionState);

      result.addEventListener('change', () => {
        setPermissionState(result.state as LocationPermissionState);
      });
    } catch (error) {
      console.error('Error checking location permission:', error);
    }
  };

  // Handle location request when user clicks continue
  const requestLocation = () => {
    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by this browser');
      return;
    }

    // Check permission before requesting location
    checkPermission();

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Extract location data on success
        const locationData: LocationData = {
          accuracy: position.coords.accuracy,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          timestamp: position.timestamp
        };

        // Notify parent components and proceed
        onLocationGranted(locationData);
        onUpload();
      },
      async (error) => {
        console.error('Location access error:', error);
        if (error.code === error.PERMISSION_DENIED) {
          try {
            // Check if permissions API is supported
            if (navigator.permissions && navigator.permissions.query) {
              const result = await navigator.permissions.query({
                name: 'geolocation'
              });

              if (result.state === 'denied') {
                // User explicitly denied in browser prompt
                setPermissionState(LocationPermissionState.DENIED);
                if (isWarningPrompt && onLocationDenied) {
                  // For WARNING_PROMPT mode, remember that user denied and don't show again
                  onLocationDenied();
                  onUpload();
                } else {
                  // For MANDATORY mode, show error
                  setShowPermissionError(true);
                }
              } else if (result.state === 'prompt') {
                // System-level permission is granted, but user denied in browser prompt
                setPermissionState(LocationPermissionState.DENIED);
                if (isWarningPrompt && onLocationDenied) {
                  // For WARNING_PROMPT mode, remember that user denied and don't show again
                  onLocationDenied();
                  onUpload();
                } else {
                  // For MANDATORY mode, show error
                  setShowPermissionError(true);
                }
              } else if (result.state === 'granted') {
                // System-level permission is granted, but something else went wrong
                console.error(
                  'Location permission granted but failed to get position'
                );
              }
            } else {
              // Fallback for browsers that don't support permissions API
              setPermissionState(LocationPermissionState.DENIED);
              if (isWarningPrompt && onLocationDenied) {
                // For WARNING_PROMPT mode, remember that user denied and don't show again
                onLocationDenied();
                onUpload();
              } else {
                // For MANDATORY mode, show error
                setShowPermissionError(true);
              }
            }
          } catch (permissionError) {
            console.error('Error checking permission state:', permissionError);
            // Fallback to basic error handling
            setPermissionState(LocationPermissionState.DENIED);
            if (isWarningPrompt && onLocationDenied) {
              // For WARNING_PROMPT mode, remember that user denied and don't show again
              onLocationDenied();
              onUpload();
            } else {
              // For MANDATORY mode, show error
              setShowPermissionError(true);
            }
          }
        }
      },
      {
        enableHighAccuracy: true, // Request high accuracy
        timeout: 5000, // Timeout after 5 seconds
        maximumAge: 0 // Don't use cached position
      }
    );
  };

  // Render browser-specific instructions for enabling location
  const renderInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      return (
        <div className="w-full text-[14px] text-grey-600 mb-8 text-left">
          <h3 className="text-[14px] font-semibold mb-4">
            iPhone or iPad (Safari or Chrome)
          </h3>
          <ol className="list-decimal pl-5 space-y-2 mb-4">
            <li>
              While on this page, tap the{' '}
              <strong>icon next to the address bar.</strong>
            </li>
            <li>
              Tap <strong>Permissions</strong> or{' '}
              <strong>Website Settings</strong> (may appear as the three-dot
              &quot;⋯&quot; menu).
            </li>
            <li>
              Tap <strong>Location</strong> and set it to <strong>Allow</strong>
              .
            </li>
            <li>Return to this page and refresh.</li>
          </ol>

          <p className="font-semibold mb-2">
            Can&apos;t find location settings in your browser? Try this instead:
          </p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Open the device <strong>Settings</strong> app.
            </li>
            <li>
              Go to <strong>Privacy & Security &gt; Location Services</strong>.
            </li>
            <li>
              Make sure <strong>Location Services</strong> is turned{' '}
              <strong>ON</strong>.
            </li>
            <li>Scroll down and tap your browser (e.g. Safari or Chrome).</li>
            <li>
              Set location access to <strong>While Using the App</strong> or{' '}
              <strong>Ask Next Time</strong>.
            </li>
            <li>Reopen your browser and refresh this page.</li>
          </ol>
        </div>
      );
    }

    return (
      <div className="w-full text-[14px] text-grey-600 mb-8 text-left">
        <h3 className="text-[14px] font-semibold mb-4">
          Android (Chrome or other browser)
        </h3>
        <ol className="list-decimal pl-5 space-y-2 mb-4">
          <li>
            While on this page, tap the{' '}
            <strong>icon next to the address bar.</strong>
          </li>
          <li>
            Tap <strong>Permissions</strong> or <strong>Site Settings</strong>.
          </li>
          <li>
            Tap <strong>Location</strong> and set it to <strong>Allow</strong>.
          </li>
          <li>Return to your inspection and refresh.</li>
        </ol>

        <p className="font-semibold mb-2">
          If you&apos;re still not prompted, check your phone settings:
        </p>
        <ol className="list-decimal pl-5 space-y-2">
          <li>
            Open the <strong>Settings</strong> app.
          </li>
          <li>
            Go to{' '}
            <strong>
              Apps &gt; [Your browser] &gt; Permissions &gt; Location
            </strong>
            .
          </li>
          <li>
            Make sure location access is set to{' '}
            <strong>Allow while using</strong>.
          </li>
        </ol>
      </div>
    );
  };

  // Component render
  return (
    <div className="bg-grey-50 min-h-screen">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 h-[72px] bg-white border-b border-gray-200 flex items-center justify-between px-4">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Go back">
            <ArrowLeftIcon className="w-6 h-6 text-gray-600 transform scale-x-[-1]" />
          </button>
        )}
        <h1 className="text-[18px] font-semibold flex-1 text-center">
          Location Sharing
        </h1>
        <div className="w-8 h-8"></div>
      </header>

      {/* Main Content */}
      <div className="flex flex-col items-center p-6 max-w-md mx-auto pt-[calc(72px+1rem)] pb-[calc(5rem+env(safe-area-inset-bottom))]">
        <LocationIcon
          className="w-full h-[71px] mb-4 mt-8"
          style={{ fill: primaryColor, color: primaryColor }}
        />

        <h2 className="text-[16px] font-bold text-grey-800 mb-4">
          {isWarningPrompt
            ? 'Location Access Recommended'
            : 'Location Access Required'}
        </h2>

        <p className="text-gray-600 mb-4 text-[14px]">
          {isWarningPrompt
            ? 'Sharing your location helps us confirm the location of the vehicle at the time you submit your photos. You can continue without sharing, but this may affect the inspection outcome.'
            : 'To complete your inspection, we need access to your location. This helps us confirm where the vehicle is at the time you submit your photos.'}
        </p>
        <div className="bg-[#F2F4F7] p-3 mb-4 rounded-lg w-full">
          <h3 className="text-[14px] text-grey-800 font-semibold mb-4 text-center">
            Your Privacy Matters
          </h3>
          <ul className="space-y-3 text-[14px] text-grey-600 pl-4 list-disc">
            <li>
              Your location is captured <strong>only once</strong>, at the
              moment you submit your vehicle photos.
            </li>
            <li>
              We <strong>do not track</strong> you continuously.
            </li>
            <li>
              Your location is <strong>not stored after</strong> the inspection
              is complete.
            </li>
            <li>
              It&apos;s used solely to support fraud prevention and to confirm
              the vehicle location.
            </li>
          </ul>
        </div>

        {/* Display permission error only after user interaction */}
        {showPermissionError &&
        permissionState === LocationPermissionState.DENIED ? (
          <>
            <div
              className="bg_error_color mb-6 rounded error_text_color px-4 py-3 shadow-md border_error_color border"
              role="alert">
              <div className="flex">
                <div className="mt-[-5px]">
                  <ErrorIcon className="mr-1" />
                </div>
                <div>
                  <p className="font-semibold">Location Access Blocked</p>
                  <p className="font-normal">
                    We weren&apos;t able to access your location because
                    it&apos;s been blocked in your browser settings. To
                    continue, please follow the steps below to re-enable access.
                  </p>
                </div>
              </div>
            </div>
            {renderInstructions()}
          </>
        ) : (
          <>
            <div className="text-[14px] text-grey-600 mb-4 text-left">
              When you tap <strong>Continue</strong>, your browser will show a
              message asking for permission to access your location.
            </div>

            <div className="w-full text-[14px] text-grey-600 mb-4 text-left">
              Select <strong>&quot;Allow&quot;</strong> when prompted.
            </div>
          </>
        )}
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-grey-25 border-t border-gray-200 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-lg">
        <Button
          onClick={requestLocation}
          style={{ backgroundColor: primaryColor }}
          className={clsx(
            'align-middle w-full select-none font-sans font-bold text-center transition-all disabled:opacity-50 rounded disabled:shadow-none disabled:pointer-events-none py-[10px] px-[18px]rounded shadow-none shadow-blue-500/20 hover:shadow-none hover:shadow-blue-500/40 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none normal-case grow lg:flex-grow-0 text-white text-[16px]',
            primaryColor && `!bg-[${primaryColor}]`
          )}>
          Continue
        </Button>
      </div>
    </div>
  );
};

LocationPrompt.propTypes = {
  onLocationGranted: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  locationSharing: PropTypes.oneOf(['MANDATORY', 'WARNING_PROMPT', 'SILENT']),
  onContinueWithoutLocation: PropTypes.func,
  onLocationDenied: PropTypes.func,
  onBack: PropTypes.func
};

export default LocationPrompt;
